import { useMemo, useCallback } from 'react';
import { Box, useMantineTheme } from '@mantine/core';
import {
  useDashboardStore,
  selectSortedWidgets,
  selectSortedContainers,
  selectIsEditMode,
} from '../../../../stores/dashboardStore';
import { usePreferencesStore } from '../../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../../hooks/useAccessibilityStyles';
import {
  DashboardGlobalData,
  PlacedWidget,
  PlacedItem,
  WidgetInstance,
  ContainerInstance,
} from '../types';
import { calculateLayout, calculateTotalRows, calculateCombinedLayout, calculateTotalRowsFromCombined } from './GridEngine';
import { getBreakpointConfig, GRID_COLUMNS } from './breakpoints';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { GridCell } from './GridCell';
import { WidgetWrapper } from '../components/WidgetWrapper';
import { ContainerWrapper } from '../components/ContainerWrapper';
import { useDragDrop } from '../hooks/useDragDrop';
import { FadeIn } from '../../../animations';

// ============================================================================
// DashboardGrid Component
// ============================================================================

export interface DashboardGridProps {
  /** Global dashboard data for widgets */
  globalData?: DashboardGlobalData;
}

export function DashboardGrid({ globalData }: DashboardGridProps) {
  const theme = useMantineTheme();
  const { breakpoint, breakpointKey } = useBreakpoint();
  const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
  const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

  const accessibilityStyles = {
    lineHeight: LINE_HEIGHT_MAP[lineSpacing],
    letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
  };

  // Store state
  const widgets = useDashboardStore(selectSortedWidgets);
  const containers = useDashboardStore(selectSortedContainers);
  const isEditMode = useDashboardStore(selectIsEditMode);

  // Drag and drop
  const {
    draggedId,
    dragOverIndex,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragEnd,
  } = useDragDrop();

  // Calculate combined layout positions (widgets + containers)
  const layout = useMemo(
    () => calculateCombinedLayout(widgets, containers, breakpointKey),
    [widgets, containers, breakpointKey]
  );

  // Get grid configuration
  const gridConfig = getBreakpointConfig(breakpointKey);
  const totalRows = calculateTotalRowsFromCombined(layout);

  // Grid styles - use explicit gridTemplateRows to prevent overlapping
  const gridStyles = useMemo(
    () => ({
      display: 'grid',
      gridTemplateColumns: `repeat(${GRID_COLUMNS}, 1fr)`,
      gridTemplateRows: totalRows > 0
        ? `repeat(${totalRows}, ${gridConfig.rowHeight}px)`
        : `repeat(10, ${gridConfig.rowHeight}px)`,
      gap: `${gridConfig.gap}px`,
      marginTop: theme.spacing.md,
    }),
    [gridConfig, theme.spacing.md, totalRows]
  );

  // Find widget instance by instanceId
  const getWidgetInstance = useCallback(
    (instanceId: string): WidgetInstance | undefined => {
      return widgets.find((w) => w.instanceId === instanceId);
    },
    [widgets]
  );

  // Find container instance by instanceId
  const getContainerInstance = useCallback(
    (instanceId: string): ContainerInstance | undefined => {
      return containers.find((c) => c.instanceId === instanceId);
    },
    [containers]
  );

  // Render all items (widgets and containers) in grid
  const renderItems = useCallback(() => {
    return layout.map((placedItem: PlacedItem, index: number) => {
      if (placedItem.type === 'widget') {
        const widgetInstance = getWidgetInstance(placedItem.item.instanceId);
        if (!widgetInstance) return null;

        const isDragging = draggedId === placedItem.item.instanceId;
        const isDragOver = dragOverIndex === index && draggedId !== placedItem.item.instanceId;

        return (
          <GridCell
            key={placedItem.item.instanceId}
            position={placedItem.item.position}
            breakpoint={breakpointKey}
            isDragging={isDragging}
            isDragOver={isDragOver}
            isEditMode={isEditMode}
          >
            <WidgetWrapper
              instance={widgetInstance}
              globalData={globalData}
              isEditMode={isEditMode}
              isDragging={isDragging}
              onDragStart={(e) => handleDragStart(e, placedItem.item.instanceId)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
            />
          </GridCell>
        );
      } else {
        // Container type
        const containerInstance = getContainerInstance(placedItem.item.instanceId);
        if (!containerInstance) return null;

        const isDragging = draggedId === placedItem.item.instanceId;
        const isDragOver = dragOverIndex === index && draggedId !== placedItem.item.instanceId;

        return (
          <GridCell
            key={placedItem.item.instanceId}
            position={placedItem.item.position}
            breakpoint={breakpointKey}
            isDragging={isDragging}
            isDragOver={isDragOver}
            isEditMode={isEditMode}
          >
            <ContainerWrapper
              instance={containerInstance}
              globalData={globalData}
              isEditMode={isEditMode}
              isDragging={isDragging}
              onDragStart={(e) => handleDragStart(e, placedItem.item.instanceId)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDrop={handleDrop}
            />
          </GridCell>
        );
      }
    });
  }, [
    layout,
    getWidgetInstance,
    getContainerInstance,
    draggedId,
    dragOverIndex,
    breakpointKey,
    isEditMode,
    globalData,
    handleDragStart,
    handleDragEnd,
    handleDragOver,
    handleDrop,
  ]);

  // Empty state - check both widgets and containers
  if (widgets.length === 0 && containers.length === 0) {
    return (
      <FadeIn>
        <Box
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 400,
            padding: theme.spacing.xl,
            backgroundColor: theme.colors.gray[0],
            borderRadius: theme.radius.lg,
            border: `2px dashed ${theme.colors.gray[3]}`,
          }}
        >
          <Box
            style={{
              fontSize: theme.fontSizes.lg,
              fontWeight: 500,
              color: theme.colors.gray[6],
              marginBottom: theme.spacing.xs,
              ...accessibilityStyles,
            }}
          >
            No widgets added
          </Box>
          <Box
            style={{
              fontSize: theme.fontSizes.sm,
              color: theme.colors.gray[5],
              ...accessibilityStyles,
            }}
          >
            Click &quot;Add Widget&quot; to customize your dashboard
          </Box>
        </Box>
      </FadeIn>
    );
  }

  return (
    <Box
      style={gridStyles}
      data-dashboard-grid
      data-breakpoint={breakpointKey}
      data-edit-mode={isEditMode}
    >
      {renderItems()}
    </Box>
  );
}

export default DashboardGrid;
